import { z } from 'zod';
import { BOSS_ABILITY_KINDS } from './bosses.js';
/**
 * Shefira's last stand (ADR 0068): the Aire, the fight's numbers, the
 * Company's posts round the arena and the scripted allies of the story.
 * Data, not code (CLAUDE.md rule 4): `rules/shefira.ts` reads it, the
 * server's `ShefiraDirector` keeps the clock.
 */
/** The Aire's zone: an instance (ADR 0068 §1), one room per fight. */
export const SHEFIRA_ZONE_ID = 'shefira-aerie';
/** Its name on the maps and in the chat. */
export const SHEFIRA_AERIE_NAME = 'L’Aire de Shefira';
export const SHEFIRA_MOB_ID = 'shefira';
/** Her brood, called down while she flies (phase 2). */
export const EMBER_WHELP_ID = 'ember-whelp';
/**
 * The story's quest (ADR 0068 §4): while it is under way and its `stand` is
 * not flagged, the crevasse lets its holder in alone, with the Company. The
 * quest itself is written with the Company's finale (`quests/company-finale.ts`).
 */
export const LAST_STAND_QUEST_ID = 'company-last-stand';
/** The two ways into the Aire: once alone for the story, then as a raid. */
export const AERIE_MODES = ['story', 'raid'];
/** A raid is three to ten (ADR 0068 §5): a party of 3 to 5, or two allied parties. */
export const RAID_MIN_PLAYERS = 3;
export const RAID_MAX_PLAYERS = 10;
/** Shefira is the content of the level cap, not one more level. */
export const RAID_LEVEL = 20;
/** Two parties at most, of their leaders' choosing (ADR 0068 §5). */
export const ALLIANCE_MAX_PARTIES = 2;
/** How long an alliance's invitation waits for the other leader. */
export const ALLIANCE_INVITE_TTL_MS = 60_000;
/** How close to the crevasse the one who enters stands, diagonals included. */
export const AERIE_GATE_REACH = 3;
/** How close to it every member of a raid gathers: all present at the entrance. */
export const AERIE_GATHER_REACH = 10;
/** Share of her health where each phase begins: the ground, then the sky, then her rage. */
export const SHEFIRA_THRESHOLDS = {
    flight: 0.6,
    enraged: 0.3,
};
/** The flight of phase 2 (ADR 0068 §3). */
export const SHEFIRA_FLIGHT = {
    /** She rises this long before she is out of reach, the time for her `takeoff`. */
    takeoffMs: 2000,
    /** Airborne at most this long, then she comes down of her own accord. */
    maxAirborneMs: 26_000,
    /** Coming down: this long after the landing is told, she stands and may be struck. */
    landingMs: 2500,
    /** On the ground between two flights of phase 2, before she rises again. */
    groundedMs: 18_000,
    /** Two harpoons this close together, from opposite ballistae, pin her down. */
    harpoonWindowMs: 9000,
    /** Her brood comes down again every so often while she flies. */
    whelpsEveryMs: 12_000,
};
/** Phase 3: the ring of fire closes in (ADR 0068 §3), Chebyshev rings round her. */
export const SHEFIRA_FIRE_RING = {
    /** The first ring spares this much round her; the arena is fifteen tiles wide on each side. */
    firstSafe: 11,
    /** It never spares less than this: the last stand is fought close. */
    lastSafe: 4,
    /** One ring less each time it comes back. */
    step: 1,
    everyMs: 11_000,
    windupMs: 2600,
    /**
     * Blows of her bite a player caught outside the safe disc takes, like a
     * shape's `multiplier` (the raid's; the story lightens it by `shapes`).
     */
    multiplier: 12,
};
/** Her abilities hasten this much once enraged, like a zone boss's attack interval. */
export const SHEFIRA_ENRAGE_HASTE = 0.7;
/** At most this many shapes marked at once (the second only from six players on). */
export const SHEFIRA_MAX_SHAPES = 2;
/** Two shapes never land closer together than this, unless the twin shapes of a big raid. */
export const SHEFIRA_ABILITY_GAP_MS = 1500;
/** Her first shape comes this long after the fight starts: the time to spread out. */
export const SHEFIRA_FIRST_ABILITY_MS = 5000;
/**
 * One of her shapes (ADR 0029): the zone bosses' vocabulary, with a dragon's
 * reach. The same fields as `BossAbility`, looser bounds: the arena is twice
 * a dungeon hall's, and a dive falls on a whole group.
 */
export const ShefiraAbilitySchema = z
    .object({
    id: z.string().regex(/^[a-z][a-z-]{0,31}$/),
    kind: z.enum(BOSS_ABILITY_KINDS),
    name: z.string().min(1),
    radius: z.number().int().min(0).max(6),
    inner: z.number().int().min(0).max(6).optional(),
    length: z.number().int().min(1).max(16).optional(),
    count: z.number().int().min(1).max(16).optional(),
    spread: z.number().int().min(1).max(14).optional(),
    windupMs: z.number().int().min(600).max(4000),
    everyMs: z.number().int().min(3000),
    multiplier: z.number().positive().max(16),
    /** A dive falls on one player picked by the server, not on her target: the sky has no tank. */
    dive: z.boolean().default(false),
})
    .refine((a) => (a.kind !== 'ring' || (a.inner !== undefined && a.inner < a.radius)) &&
    ((a.kind !== 'line' && a.kind !== 'cross') || a.length !== undefined) &&
    (a.kind !== 'rain' || (a.count !== undefined && a.spread !== undefined)), 'a ring needs its inner radius, a line or a cross its length, a rain its count and spread');
/**
 * Her shapes by phase. On the ground: her breath down a lane, her tail all
 * round (hug her or keep away), her wings, and embers raining; in the air:
 * her dives on a player and a rain of fire; enraged, the ground's shapes
 * again, faster (`SHEFIRA_ENRAGE_HASTE`), with the ring of fire besides.
 * The rain's `count` is the base the raid's size raises (`raidScaling`).
 *
 * The multipliers are the raid's and count in blows of her bite: a shape is
 * marked seconds ahead, so standing in it is a mistake and must cost. For a
 * Warrior of level 20 in the Peaks' legendary set at +10 (593 health, 37 %
 * absorbed), a clean dive takes ~60 %, the breath ~55 %, the ring of fire
 * and the tail ~45–50 %, the wings ~35 %, a drop of rain ~30 %: two big
 * shapes in a row kill, none does from full health (tests in `shefira.test.ts`).
 * The story lightens them all by its `shapes` (`STORY_SCALING`).
 */
const ABILITIES = {
    ground: [
        {
            id: 'breath',
            kind: 'line',
            name: 'Souffle de Shefira',
            radius: 1,
            length: 12,
            windupMs: 1800,
            everyMs: 9000,
            multiplier: 13,
        },
        {
            id: 'tail',
            kind: 'ring',
            name: 'Coup de queue',
            radius: 4,
            inner: 1,
            windupMs: 1700,
            everyMs: 12_000,
            multiplier: 10.5,
        },
        {
            id: 'wings',
            kind: 'nova',
            name: 'Rafale d’ailes',
            radius: 2,
            windupMs: 1500,
            everyMs: 14_000,
            multiplier: 8,
        },
        {
            id: 'embers',
            kind: 'rain',
            name: 'Pluie de braises',
            radius: 1,
            count: 4,
            spread: 12,
            windupMs: 2000,
            everyMs: 11_000,
            multiplier: 7,
        },
    ],
    flight: [
        {
            id: 'dive',
            kind: 'slam',
            name: 'Piqué',
            radius: 3,
            windupMs: 2200,
            everyMs: 8000,
            multiplier: 14,
            dive: true,
        },
        {
            id: 'firefall',
            kind: 'rain',
            name: 'Pluie de feu',
            radius: 1,
            count: 5,
            spread: 13,
            windupMs: 2000,
            everyMs: 7000,
            multiplier: 7.5,
        },
    ],
};
export const SHEFIRA_ABILITIES = {
    ground: ABILITIES.ground.map((a) => ShefiraAbilitySchema.parse(a)),
    flight: ABILITIES.flight.map((a) => ShefiraAbilitySchema.parse(a)),
};
/**
 * The Company's gear left in the arena (ADR 0068 §5): each post gives the
 * Warriors a role. `cooldownMs` is the post's own, whoever used it.
 * - `ballista` (Nella's): a harpoon in her while she flies; two from opposite
 *   ballistae within `SHEFIRA_FLIGHT.harpoonWindowMs` pin her down.
 * - `frost-brazier` (Ulfar's): a veil of frost, `guarded` (ADR 0048) for the
 *   players within `radius` for `durationMs`, against the breath and the rain.
 * - `horn` (Maëlle's): the next dive is told `extendMs` earlier to everyone.
 */
export const STATION_KINDS = ['ballista', 'frost-brazier', 'horn'];
export const STATION_RULES = {
    ballista: { cooldownMs: 15_000 },
    'frost-brazier': { cooldownMs: 24_000, radius: 2, durationMs: 5000, power: 0.5 },
    horn: { cooldownMs: 25_000, extendMs: 1800 },
};
/** How close to a post a player stands to use it, diagonals included. */
export const STATION_REACH = 2;
/**
 * The Company's allies of the story (ADR 0068 §4), in the order they stand.
 * Arena copies of their creatures (`aerie-<mob>`, `data/shefira-mobs.ts`),
 * scripted, not an AI: the director walks each where its `stance` wants it
 * round her (beside her, or a few tiles off), has it strike her for `dps`
 * while she stands on the ground, work its `post` when the fight asks for it
 * (Maëlle's horn and Nella's ballista while she flies, Ulfar's brazier before
 * her breath and her rains), lend the fight its `role` while it lives, and
 * wears its health down toward `fallsAt` of hers. There it says its `line`,
 * then falls (`out`: thrown out of the fight alive). `sword` is Ser
 * Givrecoeur's: at that share he walks to her and plants it, she flees, the
 * story is won, and he dies of his burns.
 * Lines are placeholders in the lore's epic tone; the writers will refine them.
 */
export const ALLY_ROLES = ['horn', 'roots', 'harpoon', 'ward', 'line', 'sword'];
export const StoryAllySchema = z.object({
    /** The creature it copies. */
    mob: z.string().min(1),
    /** Its arena copy's id in the bestiary. */
    arena: z.string().min(1),
    role: z.enum(ALLY_ROLES),
    /** Beside her (`melee`), or a few tiles off with a bow, a staff, a seal (`ranged`). */
    stance: z.enum(['melee', 'ranged']),
    /** The Company's post it works when the fight asks for it (ADR 0068 §5). */
    post: z.enum(STATION_KINDS).optional(),
    /** Damage per second on her while she can be struck and it is in reach. */
    dps: z.number().min(0),
    /** Her health share where it falls (or is thrown out, or plants the sword). */
    fallsAt: z.number().gt(0).lt(1),
    /** Thrown out alive rather than slain: Ulfar survives. */
    survives: z.boolean().default(false),
    /** Where it stands round her, from her home tile. */
    offset: z.object({ dx: z.number().int(), dy: z.number().int() }),
    /** What it says as it falls, French. */
    line: z.string().min(1).max(200),
});
const ALLIES = [
    {
        mob: 'lookout',
        arena: 'aerie-lookout',
        role: 'horn',
        stance: 'ranged',
        post: 'horn',
        dps: 7,
        fallsAt: 0.55,
        offset: { dx: -5, dy: 4 },
        line: 'Elle plonge ! Tout le monde à terre ! Cette fois, je l’ai vue venir !',
    },
    {
        mob: 'forest-druid',
        arena: 'aerie-forest-druid',
        role: 'roots',
        stance: 'ranged',
        dps: 6,
        fallsAt: 0.46,
        offset: { dx: 5, dy: 4 },
        line: 'Mes plantes tiennent encore ! Frappe, ne te retourne pas !',
    },
    {
        mob: 'swamp-hunter',
        arena: 'aerie-swamp-hunter',
        role: 'harpoon',
        stance: 'ranged',
        post: 'ballista',
        dps: 7,
        fallsAt: 0.38,
        offset: { dx: -6, dy: -2 },
        line: 'Je l’ai harponnée ! Aidez-moi à la tirer au sol, elle m’emporte !',
    },
    {
        mob: 'jackal-priestess',
        arena: 'aerie-jackal-priestess',
        role: 'ward',
        stance: 'ranged',
        dps: 6,
        fallsAt: 0.24,
        offset: { dx: 6, dy: -2 },
        line: 'Ma protection tient encore ! Finis-la, je ne vais pas tenir longtemps !',
    },
    {
        mob: 'snow-warden',
        arena: 'aerie-snow-warden',
        role: 'line',
        stance: 'melee',
        post: 'frost-brazier',
        dps: 8,
        fallsAt: 0.18,
        survives: true,
        offset: { dx: 0, dy: -5 },
        line: 'On tient encore ! Ne m’attends pas, gamin, continue !',
    },
    {
        mob: 'tutor',
        arena: 'aerie-tutor',
        role: 'sword',
        stance: 'melee',
        dps: 9,
        fallsAt: 0.1,
        offset: { dx: 2, dy: 3 },
        line: 'Tant que le givre tient, la Compagnie tient ! Prends ça, Shefira !',
    },
];
export const STORY_ALLIES = ALLIES.map((ally) => StoryAllySchema.parse(ally));
/** Allies strike on this beat: a steady pressure, never a burst. */
export const ALLY_STRIKE_MS = 1500;
/** While Mélisande holds them, the whelps are rooted this long at a time. */
export const ALLY_ROOT_MS = 4000;
/** While Méret's seal holds, what her shapes deal the player is multiplied by this. */
export const ALLY_WARD = 0.6;
/** While Ulfar holds the line, her bite on the player is multiplied by this: he takes the rest. */
export const ALLY_LINE = 0.5;
/**
 * How the Company moves and wears down (ADR 0068 §4), for the director and
 * the pure rules of `rules/story-allies.ts`.
 * - `thinkMs`: each ally looks again where to stand this often.
 * - `meleeBand` / `rangedBand`: the rings round her each stance stands in;
 *   `meleeReach` / `rangedReach`: the rings from which it strikes her.
 * - `rootsReach`: how far Mélisande's roots reach a whelp.
 * - `lastStand`: the share of its health an ally has left when its fall comes;
 *   `slack`: how far above its pace it may stand before she burns it down to
 *   it; `lead`: how far below its pace a shape may carry it.
 * - `lineGapMs`: allies carried past their fall by one blow speak one after
 *   the other; `lastWordsMs`: the line is said, then the ally falls this later.
 * - `chargeMs`: Ser Givrecoeur walks to her at most this long to plant the
 *   sword; `burnsMs`: he falls this long after.
 * - `limpTilesPerSecond`: Ulfar's pace, hurt, as he leaves the fight.
 * - `frostLeadMs`: Ulfar sets out for a brazier when her breath or a rain is
 *   due within this; `frostNowMs`: he lights it once one is this close, or
 *   marked; `frostWaitMs`: he gives up waiting there after this.
 * - `wardEveryMs`: Méret seals the player at most this often, as a shape is marked.
 * - `hornLead`: Maëlle goes up to her horn once Shefira is this close above her flight.
 * - `frostReach`: Ulfar only lights a brazier this close to the player: the veil is for them.
 */
export const STORY_COMPANY = {
    thinkMs: 500,
    meleeBand: { min: 1, max: 2 },
    rangedBand: { min: 3, max: 5 },
    meleeReach: 2,
    rangedReach: 7,
    rootsReach: 12,
    lastStand: 0.1,
    slack: 0.06,
    lead: 0.2,
    lineGapMs: 1600,
    lastWordsMs: 3000,
    chargeMs: 5000,
    burnsMs: 2200,
    limpTilesPerSecond: 1,
    frostLeadMs: 5000,
    frostNowMs: 1500,
    frostWaitMs: 8000,
    wardEveryMs: 3000,
    hornLead: 0.08,
    frostReach: 10,
};
/**
 * Nella's harpoon in the story: from the ballista she ran to, it hauls her
 * down this long after it bit, unless a second from the ballista across (the
 * player's) pins her at once.
 */
export const STORY_HAUL_MS = 3000;
/** The end of a fight, told to all: everyone walks out after this. */
export const AERIE_EXIT_AFTER_MS = {
    victory: 20_000,
    fled: 12_000,
    defeat: 6000,
};
/** A fallen raider gets up at the arena's entrance after this, while the fight lasts. */
export const RAID_RESPAWN_MS = 6000;
//# sourceMappingURL=shefira.js.map